import { useMemo, useState, FC, useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { ChartResponseDto, KeyMetricsResponseDto, AnalyticSearchRequestDto } from 'src/generated/services/TFinancialApi';
import { InvoiceTable } from './InvoiceTable';
import { ShipmentTable } from './ShipmentTable';
import { ChargeTable } from './ChargeTable';
import { TableTab } from './useTable';
import { useAnalyticsData } from '../context/AnalyticsDataContext';
import { UnifiedTablePagination } from 'src/presentations/components/molecules/table/UnifiedTablePagination';
import FilterNav, { IOptionBase } from './FilterNav';
import { sanitizeFormValues } from '../utils/sanitizeFormValues';
import {
  ANALYTICS_SHIPMENTS_DEFAULT_COLUMNS,
  ANALYTICS_SHIPMENTS_STORAGE_KEY,
  ANALYTICS_INVOICES_DEFAULT_COLUMNS,
  ANALYTICS_INVOICES_STORAGE_KEY,
  ANALYTICS_CHARGES_DEFAULT_COLUMNS,
  ANALYTICS_CHARGES_STORAGE_KEY,
} from './AnalyticsTableConfig';

// Helper to get column config based on table tab
export const getColumnConfigForTab = (tableTab: TableTab) => {
  const configMap: Record<TableTab, { defaultColumns: any[]; storageKey: string }> = {
    [TableTab.SHIPMENTS]: {
      defaultColumns: ANALYTICS_SHIPMENTS_DEFAULT_COLUMNS,
      storageKey: ANALYTICS_SHIPMENTS_STORAGE_KEY,
    },
    [TableTab.INVOICES]: {
      defaultColumns: ANALYTICS_INVOICES_DEFAULT_COLUMNS,
      storageKey: ANALYTICS_INVOICES_STORAGE_KEY,
    },
    [TableTab.CHARGES]: {
      defaultColumns: ANALYTICS_CHARGES_DEFAULT_COLUMNS,
      storageKey: ANALYTICS_CHARGES_STORAGE_KEY,
    },
    [TableTab.SAVINGS]: {
      defaultColumns: ANALYTICS_INVOICES_DEFAULT_COLUMNS,
      storageKey: ANALYTICS_INVOICES_STORAGE_KEY,
    },
    [TableTab.VOLUMETRIC]: {
      defaultColumns: ANALYTICS_INVOICES_DEFAULT_COLUMNS,
      storageKey: ANALYTICS_INVOICES_STORAGE_KEY,
    },
  };

  return configMap[tableTab] || configMap[TableTab.INVOICES];
};

export const AnalyticsTable: FC<{
  tableTab?: TableTab;
  visitedTabs?: Set<TableTab>;
  hideFilterNav?: boolean;
}> = ({ tableTab: externalTableTab, visitedTabs: externalVisitedTabs, hideFilterNav = false }) => {
  // Get data from context
  const { chartData } = useAnalyticsData();
  // Tab state
  const [internalTableTab, setInternalTableTab] = useState<TableTab>(TableTab.SHIPMENTS);
  const tableTab = externalTableTab ?? internalTableTab;
  const setTableTab = useMemo(() => (externalTableTab ? () => {} : setInternalTableTab), [externalTableTab]);

  // Pagination state (shared across all tabs)
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const page = useMemo(() => ({ pageNumber, pageSize }), [pageNumber, pageSize]);

  // Track which tabs have been visited to enable lazy loading
  const [internalVisitedTabs, setInternalVisitedTabs] = useState<Set<TableTab>>(new Set([TableTab.SHIPMENTS]));
  const visitedTabs = externalVisitedTabs ?? internalVisitedTabs;
  const setVisitedTabs = useMemo(() => (externalVisitedTabs ? () => {} : setInternalVisitedTabs), [externalVisitedTabs]);

  // Form context
  const { watch } = useFormContext();
  const { name, description, ...formValues } = watch();
  const dateReference = formValues?.dateReference;
  const currentCurrency = formValues?.currencyReference;

  // OPTIMIZATION: Exclude granularity from table queries (tables don't need granularity)
  const formValuesWithoutGranularity = useMemo(() => {
    const { granularity, ...rest } = formValues;
    return rest;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify({ ...formValues, granularity: undefined })]);

  const sanitizedFormValues = useMemo(() => sanitizeFormValues(formValuesWithoutGranularity), [formValuesWithoutGranularity]);

  // Get shared state from context
  const { filters: sharedFilters, updateFilter: handleFilterChange, totals } = useAnalyticsData();

  // Handle tab change and track visited tabs for lazy loading
  const handleTabChange = useCallback(
    (newTab: TableTab) => {
      setTableTab(newTab);
      setPageNumber(1); // Reset page number when switching tabs
      setVisitedTabs((prev) => new Set(prev).add(newTab)); // Add new tab to visited tabs
    },
    [setTableTab, setVisitedTabs]
  );

  // Handle page size change
  const handlePageSizeChange = useCallback((newPageSize: number) => {
    setPageSize(newPageSize);
    setPageNumber(1); // Reset to first page when changing page size
  }, []);

  // Tab options for FilterNav
  const optionTabs: IOptionBase[] = useMemo(() => {
    const tabCounts = {
      [TableTab.SHIPMENTS]: totals.shipments,
      [TableTab.INVOICES]: totals.invoices,
      [TableTab.CHARGES]: totals.charges,
    };

    return [TableTab.SHIPMENTS, TableTab.INVOICES, TableTab.CHARGES].map((tab) => ({
      label: tab,
      count: tabCounts[tab],
      info: tab,
      value: tab,
    }));
  }, [totals]);

  // Get current table total for pagination
  const currentTotal = totals[tableTab.toLowerCase() as keyof typeof totals] ?? 0;

  return (
    <div className='w-full h-[100px] flex-1 flex flex-col'>
      <div className='flex-1 shadow-[0_10px_24px_0_rgba(0,0,0,0.1)] flex flex-col overflow-hidden'>
        {!hideFilterNav && (
          <FilterNav
            total={currentTotal}
            pageNumber={pageNumber}
            setPageNumber={setPageNumber}
            tableTab={tableTab}
            optionTabs={optionTabs}
            setTableTab={handleTabChange}
            refetchData={() => {}} // Refetch is now handled by individual table components
            pageSize={page.pageSize}
          />
        )}
        <div className='flex-1 overflow-hidden relative'>
          {/* Render ALL tables but only show the active one - allows background data fetching for counts */}
          <div className={tableTab === TableTab.INVOICES ? 'h-full' : 'hidden'}>
            <InvoiceTable
              chartData={chartData}
              page={page}
              initialSearchRequest={sanitizedFormValues}
              isTabVisited={visitedTabs.has(TableTab.INVOICES)}
              dateReference={dateReference}
              currentCurrency={currentCurrency}
              sharedFilters={sharedFilters}
              onFilterChange={handleFilterChange}
            />
          </div>

          <div className={tableTab === TableTab.SHIPMENTS ? 'h-full' : 'hidden'}>
            <ShipmentTable
              chartData={chartData}
              page={page}
              initialSearchRequest={sanitizedFormValues}
              isTabVisited={visitedTabs.has(TableTab.SHIPMENTS)}
              dateReference={dateReference}
              currentCurrency={currentCurrency}
              sharedFilters={sharedFilters}
              onFilterChange={handleFilterChange}
            />
          </div>

          <div className={tableTab === TableTab.CHARGES ? 'h-full' : 'hidden'}>
            <ChargeTable
              chartData={chartData}
              page={page}
              initialSearchRequest={sanitizedFormValues}
              isTabVisited={visitedTabs.has(TableTab.CHARGES)}
              dateReference={dateReference}
              currentCurrency={currentCurrency}
              sharedFilters={sharedFilters}
              onFilterChange={handleFilterChange}
            />
          </div>
        </div>

        {/* Pagination */}
        <div className='p-12 xl:p-16 bg-neutral-10 flex items-center justify-between border-t border-neutral-20'>
          <UnifiedTablePagination
            pageIndex={pageNumber - 1}
            pageSize={pageSize}
            totalItems={currentTotal}
            onPageChange={setPageNumber}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[25, 50, 100, 200]}
          />
        </div>
      </div>
    </div>
  );
};
